import * as React from 'react'
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { MobXProviderContext, observer } from 'mobx-react'

import { ILWFCCategory } from '~/client/src/shared/components/ListWithFixedColumns/VirtualListHelpers'
import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import { LocalStorageKey } from '~/client/src/shared/enums/LocalStorageKey'
import { useCanEditResource } from '~/client/src/shared/hooks/useCanEditResource'
import { isUnassignedFilterOptionId } from '~/client/src/shared/hooks/useFilters/filterOptionIds'
import {
  getResourceQuickFilterDefinitions,
  IQuickFilters,
  RESOURCE_QUICK_FILTER_ME_KEY,
  RESOURCE_QUICK_FILTER_MY_COMPANY_KEY,
} from '~/client/src/shared/hooks/useResourceQuickFilters'
import { useGroupingControls } from '~/client/src/shared/hooks/useTableGrouping'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import UserProject from '~/client/src/shared/models/UserProject'
import { buildResourceUiConfig } from '~/client/src/shared/resources/builders/buildResourceUiConfig'
import { IResourceRuntimeContext } from '~/client/src/shared/resources/definitions'
import {
  IResourceRecord,
  RESOURCE_NAME_KEY,
} from '~/client/src/shared/resources/definitions/types'
import { getResourceGroupPaths } from '~/client/src/shared/resources/grouping/getResourceGroupPaths'
import { collectGroupPaths } from '~/client/src/shared/resources/grouping/groupPath'

import DesktopInitialState from '../../stores/DesktopInitialState'
import { useTableFilters } from '../TableFilters'
import { useTableGroupSort } from '../TableGroupSort'
import {
  ResourceActions,
  ResourceContext,
  ResourceMeta,
  ResourceState,
} from './context/ResourceContext'
import { useTableColumns } from './hooks/useTableColumns'
import type { ResourceSourceHook } from './resourceSource'

export interface ResourceProviderConfig {
  readonly resourceTypeId: ResourceTypeId
  readonly columnConfigKey: LocalStorageKey
  readonly useRuntimeContext: () => IResourceRuntimeContext
  readonly useSource: ResourceSourceHook
}

interface ResourceProviderProps {
  projectId: string
  userId: string
  state: DesktopInitialState
  children: React.ReactNode
}

const noop = () => undefined

export function createResourceProvider(
  config: ResourceProviderConfig,
): React.FC<ResourceProviderProps> {
  const uiConfig = buildResourceUiConfig(config.resourceTypeId)
  const useSource = config.useSource

  const ResourceProvider: React.FC<ResourceProviderProps> = ({
    projectId,
    userId,
    state,
    children,
  }) => {
    const runtimeContext = config.useRuntimeContext()

    const [selectedRecordId, setSelectedRecordId] = useState<string | null>(
      null,
    )
    const [chosenTemplateId, setChosenTemplateId] = useState<string | null>(
      null,
    )
    // Bumped when the user moves on to another panel, so a save that resolves after
    // that leaves the panel alone.
    const navigationRef = useRef(0)
    const [pendingCreatedRecordId, setPendingCreatedRecordId] = useState<
      string | null
    >(null)
    const [isBulkEditPanelOpen, setIsBulkEditPanelOpen] = useState(false)
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
    const groupingControls = useGroupingControls()

    const {
      records,
      loading,
      save: saveRecord,
      bulkEdit: bulkEditRecords,
      deleteMany: deleteRecord,
      schema,
      store,
    } = useSource(projectId)

    const {
      columns,
      configuredColumns,
      defaultColumns,
      sortingOptions,
      groupingOptions,
      behaviorColumns,
      excludedFromSortKeys,
      resetColumnsConfiguration,
      updateColumnsConfiguration,
      fixedColumnsCount,
      columnWidths,
      onColumnResize,
      loading: schemaLoading,
      fieldCatalog,
      fieldProjector,
    } = useTableColumns({
      projectId,
      resourceTypeId: config.resourceTypeId,
      userId,
      columnConfigKey: config.columnConfigKey,
      resourceRuntimeContext: runtimeContext,
      records,
      properties: schema.properties,
      schemaLoading: schema.loading,
    })

    const { userProjectsStore } = useContext(MobXProviderContext) as any
    const currentUserCompanyId = UserProject.getCompanyId(
      state?.user,
      userProjectsStore,
    )

    const userPropertyKeys = useMemo(
      () =>
        columns
          .filter(c => c.propertyConfig?.propertyType === 'user')
          .map(c => c.dataKey.replace(/^data\./, '')),
      [columns],
    )
    const companyPropertyKeys = useMemo(
      () =>
        columns
          .filter(c => c.propertyConfig?.propertyType === 'company')
          .map(c => c.dataKey.replace(/^data\./, '')),
      [columns],
    )

    const quickFilterDefinitions = useMemo(
      () =>
        getResourceQuickFilterDefinitions({
          resourceTypeId: config.resourceTypeId,
          userPropertyKeys,
          companyPropertyKeys,
          currentUserId: userId,
          currentUserCompanyId,
        }),
      [
        config.resourceTypeId,
        userPropertyKeys,
        companyPropertyKeys,
        userId,
        currentUserCompanyId,
      ],
    )

    const filterColumns = useMemo(() => {
      const resourceColumns = [...columns, ...behaviorColumns]
      const columnsByFieldKey = new Map(
        resourceColumns.map(column => [
          column.dataKey.startsWith('data.')
            ? column.dataKey.replace(/^data\./, '')
            : column.dataKey,
          column,
        ]),
      )
      const filterableKeys = new Set(
        fieldCatalog.filterableFields.map(field => field.key),
      )
      const orderedFields = [
        ...fieldCatalog.filterableFields,
        ...fieldCatalog.searchableFields.filter(
          field => !filterableKeys.has(field.key),
        ),
      ]
      const orderedResourceColumns = orderedFields
        .map(field => columnsByFieldKey.get(field.key))
        .filter(
          (column): column is (typeof resourceColumns)[number] => !!column,
        )

      return [
        ...orderedResourceColumns,
        ...quickFilterDefinitions.map(definition => ({
          dataKey: definition.key,
          label: definition.label,
          accessorFn: (record: IResourceRecord) =>
            definition.matchesRecord(record) ? [definition.optionId] : [],
          isFacetable: true,
          isSearchable: false,
          isHiddenInFilterUI: true,
        })),
      ]
    }, [
      columns,
      behaviorColumns,
      fieldCatalog.filterableFields,
      fieldCatalog.searchableFields,
      quickFilterDefinitions,
    ])

    const getOptionName = useCallback(
      (optionId: string, facetKey: string) => {
        if (facetKey === RESOURCE_QUICK_FILTER_ME_KEY) {
          return Localization.translator.me
        }
        if (facetKey === RESOURCE_QUICK_FILTER_MY_COMPANY_KEY) {
          return Localization.translator.myCo
        }
        if (isUnassignedFilterOptionId(optionId)) {
          return Localization.translator.unassigned
        }

        const column = [...columns, ...behaviorColumns].find(
          col => col.dataKey === facetKey,
        )
        const formatter =
          column?.facetDisplayFormatter || column?.displayFormatter

        return formatter ? formatter(optionId) : optionId
      },
      [columns, behaviorColumns],
    )

    const { filterProps, applyFilters, hasActiveFilters, setAppliedFilters } =
      useTableFilters({
        records: records || [],
        columns: filterColumns,
      })

    const quickFilters: IQuickFilters = useMemo(() => {
      const meDefinition = quickFilterDefinitions[0]
      const myCompanyDefinition = quickFilterDefinitions[1]

      const toggleQuickFilter = (key: string, optionId: string) => {
        const isSelected = filterProps.appliedFilters[key]?.has(optionId)
        filterProps.onFilterApply(key, isSelected ? [] : [optionId])
      }

      return {
        isOnlyMe: !!filterProps.appliedFilters[meDefinition.key]?.has(
          meDefinition.optionId,
        ),
        isOnlyMyCompany: !!filterProps.appliedFilters[
          myCompanyDefinition.key
        ]?.has(myCompanyDefinition.optionId),
        toggleOnlyMe: () =>
          toggleQuickFilter(meDefinition.key, meDefinition.optionId),
        toggleOnlyMyCompany: () =>
          toggleQuickFilter(
            myCompanyDefinition.key,
            myCompanyDefinition.optionId,
          ),
        hasUserProperties: meDefinition.isAvailable,
        hasCompanyProperties: myCompanyDefinition.isAvailable,
        resetQuickFilters: () => {
          filterProps.onFilterApply(meDefinition.key, [])
          filterProps.onFilterApply(myCompanyDefinition.key, [])
        },
      }
    }, [filterProps, quickFilterDefinitions])

    const filteredRecords = useMemo(
      () => applyFilters(records || []),
      [applyFilters, records],
    )

    const { groupSortProps, currentGrouping, applySorting } = useTableGroupSort(
      {
        groupingOptions,
        sortingOptions,
        initialGroupByKey: uiConfig.defaultGroupingKey,
        initialSortKey:
          uiConfig.defaultSortKey === RESOURCE_NAME_KEY
            ? RESOURCE_NAME_KEY
            : `data.${uiConfig.defaultSortKey}`,
        initialSortOrder: uiConfig.defaultSortOrder,
        resourceTypeId: config.resourceTypeId,
        state,
        sortRecordsByKey: fieldProjector.sortRecords,
      },
    )

    const sortedRecords = useMemo(
      () => applySorting(filteredRecords, columns),
      [applySorting, filteredRecords, columns],
    )

    const selectRecord = useCallback((id: string) => {
      navigationRef.current += 1
      setPendingCreatedRecordId(null)
      setIsBulkEditPanelOpen(false)
      setSelectedRecordId(id)
    }, [])

    const closeEditPanel = useCallback(() => {
      navigationRef.current += 1
      setPendingCreatedRecordId(null)
      setSelectedRecordId(null)
      setChosenTemplateId(null)
    }, [])

    const createNew = useCallback(() => {
      navigationRef.current += 1
      setPendingCreatedRecordId(null)
      setIsBulkEditPanelOpen(false)
      setChosenTemplateId(null)
      setSelectedRecordId('new')
    }, [])

    const openBulkEditPanel = useCallback(() => {
      if (!selectedIds.size) return
      navigationRef.current += 1
      setPendingCreatedRecordId(null)
      setSelectedRecordId(null)
      setIsBulkEditPanelOpen(true)
    }, [selectedIds.size])

    const closeBulkEditPanel = useCallback(() => {
      setIsBulkEditPanelOpen(false)
    }, [])

    const toggleSelection = useCallback((id: string) => {
      setSelectedIds(prev => {
        const next = new Set(prev)
        if (next.has(id)) {
          next.delete(id)
        } else {
          next.add(id)
        }
        return next
      })
    }, [])

    const toggleCategorySelection = useCallback((ids: string[]) => {
      setSelectedIds(prev => {
        const next = new Set(prev)
        const allSelected = ids.every(id => next.has(id))
        if (allSelected) {
          ids.forEach(id => next.delete(id))
        } else {
          ids.forEach(id => next.add(id))
        }
        return next
      })
    }, [])

    const setSelection = useCallback((ids: Set<string>) => {
      setSelectedIds(ids)
    }, [])

    const clearSelection = useCallback(() => {
      setSelectedIds(new Set())
      setIsBulkEditPanelOpen(false)
    }, [])

    const { collapsedCategories, toggleCategory, isAllCollapsed } =
      groupingControls

    const groupKeys = useMemo(() => {
      if (!currentGrouping) return []
      const hierarchy = fieldCatalog.getField(currentGrouping)?.hierarchy
      const keySet = new Set<string>()

      sortedRecords.forEach(record => {
        const paths = getResourceGroupPaths(
          record,
          currentGrouping,
          hierarchy,
          fieldProjector,
          Localization.translator.unassigned,
        )
        collectGroupPaths(paths, currentGrouping).forEach(path =>
          keySet.add(path),
        )
      })
      return Array.from(keySet)
    }, [currentGrouping, fieldCatalog, fieldProjector, sortedRecords])

    const collapseAll = useCallback(() => {
      groupingControls.collapseAll(groupKeys)
    }, [groupingControls.collapseAll, groupKeys])

    const expandAll = useCallback(() => {
      groupingControls.expandAll(groupKeys)
    }, [groupingControls.expandAll, groupKeys])

    const handleSave = useCallback(
      async (data: Record<string, any>) => {
        const navigation = navigationRef.current
        const savedId = await saveRecord(selectedRecordId, data)
        if (navigation !== navigationRef.current) return
        setChosenTemplateId(null)
        // A new resource stays open on its own card, so its controls are a click away.
        const createdId = selectedRecordId === 'new' ? savedId : null
        setPendingCreatedRecordId(createdId)
        setSelectedRecordId(createdId)
      },
      [saveRecord, selectedRecordId],
    )

    // Once the created record arrives it is an ordinary one: losing it later closes the
    // panel rather than bringing back the creation spinner.
    useEffect(() => {
      if (records?.some(r => r.id === pendingCreatedRecordId)) {
        setPendingCreatedRecordId(null)
      }
    }, [records, pendingCreatedRecordId])

    // The last stop before a bulk or delete mutation: the buttons are already disabled for a
    // selection holding a resource the user may not change, so this only catches a path that
    // got around them. An id with no loaded record cannot be checked, so it fails too.
    const canEdit = useCanEditResource(config.resourceTypeId)
    const assertEditable = useCallback(
      (ids: readonly string[]) => {
        const recordsById = new Map(
          (records ?? []).map(record => [record.id, record]),
        )
        if (ids.some(id => !canEdit(recordsById.get(id)))) {
          throw new Error(
            Localization.translator.selectionHasResourcesYouCannotChange,
          )
        }
      },
      [records, canEdit],
    )

    const bulkEditSelected = useCallback(
      async (changes: Record<string, any>) => {
        const resourceIds = Array.from(selectedIds)
        assertEditable(resourceIds)
        await bulkEditRecords({ resourceIds, changes })
        setIsBulkEditPanelOpen(false)
        setSelectedIds(new Set())
      },
      [bulkEditRecords, selectedIds, assertEditable],
    )

    const deleteSelected = useCallback(async () => {
      const ids = Array.from(selectedIds)
      assertEditable(ids)
      await deleteRecord(ids)
      setIsBulkEditPanelOpen(false)
      setSelectedIds(new Set())
    }, [selectedIds, deleteRecord, assertEditable])

    const deleteRecords = useCallback(
      async (ids: string[]) => {
        assertEditable(ids)
        await deleteRecord(ids)
        setSelectedIds(prev => {
          const next = new Set(prev)
          ids.forEach(id => next.delete(id))
          return next
        })
        setSelectedRecordId(prev => (prev && ids.includes(prev) ? null : prev))
      },
      [deleteRecord, assertEditable],
    )

    const isSelected = useCallback(
      (id: string) => selectedIds.has(id),
      [selectedIds],
    )

    const isCategorySelected = useCallback(
      (ids: string[]) => {
        if (!ids || ids.length === 0) return false
        return ids.every(id => selectedIds.has(id))
      },
      [selectedIds],
    )

    const handleCategoryCheckboxToggle = useCallback(
      (category: ILWFCCategory) => {
        if (category.idsToSelect) {
          toggleCategorySelection(category.idsToSelect)
        }
      },
      [toggleCategorySelection],
    )

    const contextValue = useMemo(
      () => ({
        state: {
          records,
          filteredRecords: sortedRecords,
          selectedIds,
          selectedRecordId,
          pendingCreatedRecordId,
          chosenTemplateId,
          isBulkEditPanelOpen,
          appliedFilters: filterProps.appliedFilters,
          searchKey: filterProps.searchKey ?? '',
          grouping: currentGrouping,
          sorting: {
            key: groupSortProps.selectedSortingOptionId ?? 'name',
            order: groupSortProps.selectedSortOrder,
          },
          collapsedCategories,
          isAllCollapsed,
        } satisfies ResourceState,
        actions: {
          selectRecord,
          closeEditPanel,
          createNew,
          setChosenTemplateId,
          openBulkEditPanel,
          closeBulkEditPanel,
          toggleSelection,
          toggleCategorySelection,
          setSelection,
          clearSelection,
          applyFilter: filterProps.onFilterApply,
          resetFilters: filterProps.onResetAll,
          setSearchKey: filterProps.onSearchChange || noop,
          setAppliedFilters,
          setGrouping: groupSortProps.onGroupingChange,
          setSorting: groupSortProps.onSortingChange,
          toggleCategory,
          collapseAll,
          expandAll,
          save: handleSave,
          bulkEditSelected,
          deleteSelected,
          deleteRecords,
        } satisfies ResourceActions,
        meta: {
          controlCommands: store.controls,
          projectId,
          resourceTypeId: config.resourceTypeId,
          columns,
          configuredColumns,
          defaultColumns,
          columnWidths,
          onColumnResize,
          fixedColumnCount: fixedColumnsCount,
          updateColumnsConfiguration,
          resetColumnsConfiguration,
          facets: filterProps.facets,
          sourceMapByFacet: filterProps.sourceMapByFacet,
          getOptionName,
          hasActiveFilters,
          groupingOptions,
          sortingOptions,
          excludedFromSortKeys,
          isLoading: (loading && records.length === 0) || schemaLoading,
          selectedCount: selectedIds.size,
          filteredCount: sortedRecords.length,
          isSelected,
          isCategorySelected,
          onCategoryCheckboxToggle: handleCategoryCheckboxToggle,
          groupSortProps,
          filterProps,
          groupingControls,
          runtimeContext,
          fieldProjector,
          fieldCatalog,
          quickFilters,
          schema,
        } satisfies ResourceMeta,
      }),
      [
        records,
        sortedRecords,
        selectedIds,
        selectedRecordId,
        pendingCreatedRecordId,
        chosenTemplateId,
        isBulkEditPanelOpen,
        filterProps.appliedFilters,
        filterProps.searchKey,
        currentGrouping,
        groupSortProps.selectedSortingOptionId,
        groupSortProps.selectedSortOrder,
        collapsedCategories,
        isAllCollapsed,
        selectRecord,
        closeEditPanel,
        createNew,
        setChosenTemplateId,
        openBulkEditPanel,
        closeBulkEditPanel,
        toggleSelection,
        toggleCategorySelection,
        setSelection,
        clearSelection,
        filterProps.onFilterApply,
        filterProps.onResetAll,
        filterProps.onSearchChange,
        setAppliedFilters,
        groupSortProps.onGroupingChange,
        groupSortProps.onSortingChange,
        toggleCategory,
        collapseAll,
        expandAll,
        handleSave,
        bulkEditSelected,
        deleteSelected,
        deleteRecords,
        projectId,
        config.resourceTypeId,
        columns,
        configuredColumns,
        defaultColumns,
        columnWidths,
        onColumnResize,
        fixedColumnsCount,
        updateColumnsConfiguration,
        resetColumnsConfiguration,
        filterProps.facets,
        filterProps.sourceMapByFacet,
        getOptionName,
        hasActiveFilters,
        groupingOptions,
        sortingOptions,
        excludedFromSortKeys,
        loading,
        schemaLoading,
        selectedIds.size,
        isSelected,
        isCategorySelected,
        handleCategoryCheckboxToggle,
        groupSortProps,
        filterProps,
        groupingControls,
        fieldProjector,
        fieldCatalog,
        quickFilters,
        schema,
      ],
    )

    return (
      <ResourceContext.Provider value={contextValue}>
        {children}
      </ResourceContext.Provider>
    )
  }

  return observer(ResourceProvider)
}
